import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SKIP_GLOBAL_LOADER } from '../interceptors/loading.interceptor';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private http = inject(HttpClient);
  private baseUrl: string = environment.API_BASE_URL;

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    });
  }

  private buildParams(params?: Record<string, any>): HttpParams {
    let http_params = new HttpParams();
    if (!params) return http_params;
    Object.keys(params).forEach(param => {
      const val = params[param];
      if (val === null || val === undefined) return;
      if (Array.isArray(val)) {
        val.forEach(value => http_params = http_params.append(param, value));
      } else {
        http_params = http_params.set(param, val.toString());
      }
    });
    return http_params;
  }

  public get(
    endpoint: string,
    params?: Record<string, any>,
    options?: { skipGlobalLoader?: boolean }
  ): Observable<any> {
    const context = options?.skipGlobalLoader
      ? new HttpContext().set(SKIP_GLOBAL_LOADER, true)
      : undefined;
    return this.http.get(`${this.baseUrl}/${endpoint}`, {
      headers: this.getHeaders(),
      params: this.buildParams(params),
      context,
    }).pipe(catchError(this.handleError));
  }

  public getBlob(endpoint: string, params?: Record<string, any>): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${endpoint}`, {
      headers: new HttpHeaders({
        'Accept': 'text/csv,application/octet-stream,*/*',
      }),
      params: this.buildParams(params),
      responseType: 'blob',
    }).pipe(catchError(this.handleError));
  }

  public post(endpoint: string, data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  public put(endpoint: string, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  public patch(endpoint: string, data: any): Observable<any> {
    return this.http.patch(`${this.baseUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    }).pipe(catchError(this.handleError));
  }

  public delete(endpoint: string, data?: any): Observable<any> {
    return this.http.delete(`${this.baseUrl}/${endpoint}`, {
      headers: this.getHeaders(),
      body: data
    }).pipe(catchError(this.handleError));
  }

  public postWithoutHeader(
    endpoint: string,
    data: any,
    options?: {
      headers?: { [header: string]: string };
      params?: { [param: string]: string };
    }
  ): Observable<any> {
    const httpOptions: any = { observe: 'body' as const };
    if (options?.headers) {
      httpOptions.headers = new HttpHeaders(options.headers);
    }
    if (options?.params) {
      httpOptions.params = new HttpParams({ fromObject: options.params });
    }
    return this.http
      .post<any>(`${this.baseUrl}/${endpoint}`, data, httpOptions)
      .pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse) {
    let error_object = {
      error: {},
      message: 'Something went wrong. Please try again later.',
    };
    if (error.error instanceof ErrorEvent) {
      error_object.error = error.error;
      error_object.message = `Client-side Error: ${error.error.message}`;
    } else {
      error_object.error = error.error;
      error_object.message = `Server-side Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return throwError(() => error_object);
  }

}
